import { Controller } from "@hotwired/stimulus"

/**
 * Manages the user profile dropdown menu in the frontend header.
 * Handles toggling, ARIA attributes, and closing when clicking outside or pressing Escape.
 */
export default class extends Controller {
  static targets = ["button", "menu"]

  connect() {
    this.isOpen = false
    this.boundOnDocumentClick = this.onDocumentClick.bind(this)
    this.boundOnKeydown = this.onKeydown.bind(this)

    this.syncClosedState()
  }

  disconnect() {
    this.removeListeners()
  }

  /**
   * Toggles the menu visibility.
   */
  toggle(event) {
    event.preventDefault()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Opens the user menu and sets up event listeners.
   */
  open() {
    if (this.isOpen) {
      return
    }

    this.isOpen = true
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.menuTarget.setAttribute("aria-hidden", "false")
    this.menuTarget.removeAttribute("inert")

    this.menuTarget.classList.remove("pointer-events-none", "opacity-0", "translate-y-2", "scale-[0.98]")
    this.menuTarget.classList.add("pointer-events-auto", "opacity-100", "translate-y-0", "scale-100")

    document.addEventListener("click", this.boundOnDocumentClick)
    document.addEventListener("keydown", this.boundOnKeydown)

    requestAnimationFrame(() => {
      this.focusFirstItem()
    })
  }

  /**
   * Closes the user menu and cleans up event listeners.
   */
  close({ restoreFocus = true } = {}) {
    if (!this.isOpen && this.menuTarget.getAttribute("aria-hidden") === "true") {
      return
    }

    this.isOpen = false
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.menuTarget.setAttribute("aria-hidden", "true")
    this.menuTarget.setAttribute("inert", "")

    this.menuTarget.classList.add("pointer-events-none", "opacity-0", "translate-y-2", "scale-[0.98]")
    this.menuTarget.classList.remove("pointer-events-auto", "opacity-100", "translate-y-0", "scale-100")

    this.removeListeners()

    if (restoreFocus) {
      this.buttonTarget.focus()
    }
  }

  /**
   * Closes the menu if the user clicks outside the controller element.
   */
  onDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  /**
   * Closes the menu on Escape key press.
   */
  onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    }
  }

  /**
   * Ensures the menu is visually and functionally hidden on connection.
   */
  syncClosedState() {
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.menuTarget.setAttribute("aria-hidden", "true")
    this.menuTarget.setAttribute("inert", "")
    this.menuTarget.classList.add("pointer-events-none", "opacity-0", "translate-y-2", "scale-[0.98]")
    this.menuTarget.classList.remove("pointer-events-auto", "opacity-100", "translate-y-0", "scale-100")
  }

  removeListeners() {
    document.removeEventListener("click", this.boundOnDocumentClick)
    document.removeEventListener("keydown", this.boundOnKeydown)
  }

  /**
   * Moves focus to the first menu item when opened.
   */
  focusFirstItem() {
    const firstFocusable = this.focusableElements[0]

    firstFocusable?.focus?.()
  }

  /**
   * Returns a list of all focusable elements within the menu.
   */
  get focusableElements() {
    return Array.from(
      this.menuTarget.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden)
  }
}
